import { useSeoMeta, useHead } from '@unhead/react';
import {Check} from 'lucide-react';
import { Link } from 'react-router-dom';
import { SiteShell } from '@/components/SiteShell';
import { PageHero } from '@/components/PageHero';
import { normalizeCanonicalUrl } from '@/lib/seo';
import pricingBg from '../../images/VIP House Movers Removal Van.jpg';
import pricingImg1 from '../../images/VIP House Movers Team Loading Removal Van.jpg';
import pricingImg2 from '../../images/VIP House Movers Removal Boxes.jpg';
import pricingImg3 from '../../images/VIP House Movers Team Packing.jpg';
import pricingImg4 from '../../images/VIP House Movers Removal Boxes.jpg';
import pricingBanner from '../../images/VIP House Movers Team with happy customer.jpg';

export default function Pricing() {
  useSeoMeta({
    title: 'Removal Costs & Pricing | Cheap House Removals Wolverhampton | VIP House Movers',
    description: 'Transparent removal pricing in Wolverhampton & West Midlands for house removals, man and van, packing services, storage and office moves. No hidden fees. Free quote: 0121 752 7107.',
    ogTitle: 'Removal Costs & Pricing | VIP House Movers Wolverhampton',
    ogDescription: 'Honest, transparent removal pricing across the West Midlands. Man & van to full house moves. Free quotes from VIP House Movers.',
    ogImage: 'https://www.viphousemovers.com/og-home.jpg',
    ogUrl: 'https://www.viphousemovers.com/pricing',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: 'Removal Pricing | VIP House Movers Wolverhampton',
    twitterDescription: 'Transparent removal pricing in Wolverhampton & West Midlands from VIP House Movers.',
    twitterImage: 'https://www.viphousemovers.com/og-home.jpg',
  });
  const canonicalHref = normalizeCanonicalUrl('https://www.viphousemovers.com/pricing');

  useHead({
    link: [{ rel: 'canonical', href: canonicalHref }],
    meta: [
      {
        name: 'keywords',
        content: 'removal pricing Wolverhampton, removals cost calculator, house removals pricing, man and van pricing, storage pricing, affordable removals, cheap removals West Midlands',
      },
    ],
    script: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://www.viphousemovers.com',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Pricing',
              item: 'https://www.viphousemovers.com/pricing',
            },
          ],
        }),
      },
    ],
  });
  return (
    <SiteShell activePath="/pricing" fixedNav>
      <PageHero
        eyebrow="VIP House Movers Pricing"
        title="Simple, transparent pricing"
        description="No hidden fees. Just honest rates for a premium, fully managed VIP moving experience tailored to your property and schedule."
        align="center"
        primaryLabel="Get a Tailored Quote"
        primaryTo="/quote"
        secondaryLabel="Speak to Our Team"
        secondaryTo="/contact"
        backgroundImage={pricingBg}
      />

      <div className="max-w-7xl mx-auto px-4 py-20">
        {/* Trust strip with images */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {[
            { src: pricingImg1, alt: 'VIP House Movers professional team with loaded removal van ready for a UK house move' },
            { src: pricingImg2, alt: 'VIP House Movers organised moving boxes labelled room-by-room for a stress-free relocation' },
            { src: pricingImg3, alt: 'VIP House Movers packer wrapping fragile china in protective materials' },
            { src: pricingImg4, alt: 'VIP House Movers secure self-storage facility for short and long term storage' },
          ].map((img) => (
            <div key={img.src} className="relative overflow-hidden rounded-xl aspect-[4/3] group shadow-sm">
              <img
                src={img.src}
                alt={img.alt}
                width={700}
                height={525}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Man & Van</h3>
            <p className="text-gray-500 mb-6">Perfect for small moves or single items.</p>
            <div className="text-4xl font-bold text-[#D4AF37] mb-6">Call Us</div>
            <ul className="space-y-4 mb-8 text-gray-600">
              <li className="flex items-center gap-3"><Check className="text-green-500 w-5 h-5"/> 1 Professional Mover</li>
              <li className="flex items-center gap-3"><Check className="text-green-500 w-5 h-5"/> Luton Van</li>
              <li className="flex items-center gap-3"><Check className="text-green-500 w-5 h-5"/> Goods in transit insurance</li>
              <li className="flex items-center gap-3"><Check className="text-green-500 w-5 h-5"/> Fuel included (local)</li>
            </ul>
            <Link to="/quote" className="block text-center w-full border-2 border-[#D4AF37] text-gray-900 font-bold py-3 rounded-lg hover:bg-[#D4AF37] transition-colors">Get Quote</Link>
          </div>

          <div className="bg-gray-900 rounded-2xl shadow-xl p-8 relative transform md:-translate-y-4">
            <div className="absolute top-0 right-0 bg-[#D4AF37] text-gray-900 font-bold px-3 py-1 text-sm rounded-bl-lg rounded-tr-xl">POPULAR</div>
            <h3 className="text-2xl font-bold text-white mb-2">Full House Move</h3>
            <p className="text-gray-400 mb-6">Ideal for 2-3 bedroom residential homes.</p>
            <div className="text-4xl font-bold text-[#D4AF37] mb-6">Custom</div>
            <ul className="space-y-4 mb-8 text-gray-300">
              <li className="flex items-center gap-3"><Check className="text-[#D4AF37] w-5 h-5"/> 2-3 Professional Movers</li>
              <li className="flex items-center gap-3"><Check className="text-[#D4AF37] w-5 h-5"/> Large moving vehicle</li>
              <li className="flex items-center gap-3"><Check className="text-[#D4AF37] w-5 h-5"/> Disassembly & reassembly</li>
              <li className="flex items-center gap-3"><Check className="text-[#D4AF37] w-5 h-5"/> Full comprehensive insurance</li>
            </ul>
            <Link to="/quote" className="block text-center w-full bg-[#D4AF37] text-gray-900 font-bold py-3 rounded-lg hover:bg-yellow-500 transition-colors">Get Custom Quote</Link>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">VIP Packing & Move</h3>
            <p className="text-gray-500 mb-6">The ultimate hands-off, stress-free experience.</p>
            <div className="text-4xl font-bold text-[#D4AF37] mb-6">Premium</div>
            <ul className="space-y-4 mb-8 text-gray-600">
              <li className="flex items-center gap-3"><Check className="text-green-500 w-5 h-5"/> Full packing service</li>
              <li className="flex items-center gap-3"><Check className="text-green-500 w-5 h-5"/> All materials included</li>
              <li className="flex items-center gap-3"><Check className="text-green-500 w-5 h-5"/> Dedicated moving team</li>
              <li className="flex items-center gap-3"><Check className="text-green-500 w-5 h-5"/> Unpacking at destination</li>
            </ul>
            <Link to="/quote" className="block text-center w-full border-2 border-[#D4AF37] text-gray-900 font-bold py-3 rounded-lg hover:bg-[#D4AF37] transition-colors">Contact Us</Link>
          </div>

        </div>

        {/* What’s included banner */}
        <div className="mt-16 overflow-hidden rounded-3xl border border-gray-100 shadow-sm bg-white grid lg:grid-cols-2">
          <img
            src={pricingBanner}
            alt="VIP House Movers providing full packing and removal service for a family in Wolverhampton"
            width={1200}
            height={900}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover min-h-[320px]"
          />
          <div className="p-8 md:p-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">What’s included in every VIP move?</h3>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Transparent pricing, uniformed movers, goods in transit insurance, disassembly and reassembly of standard furniture, floor and wall protection, and friendly support from first call to final box.
            </p>
            <ul className="space-y-3 text-gray-700">
              {['Fully insured team and vehicles','Free, no-obligation quotes','No hidden fees or surprise charges','Eco-friendly packing materials'].map((item) => (
                <li key={item} className="flex items-center gap-3"><Check className="text-green-600 w-5 h-5"/>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </SiteShell>
  );
}

